/**
 * Import Monitor — Scheduled Jobs API (CR-8213).
 *
 * The run log of nightly scripts (e.g. auto-acknowledging PO lines), kept for
 * 30 days. Its own module rather than an addition to `imports.ts`, which is
 * already past the 500-line limit.
 */
import api from './client';
import type { CommandResult, CommandResultTile, CommandRunStatus } from './commandRunner';

/** One logged run of a scheduled script. */
export interface ScheduledJobRun {
  id: number;
  /** Management command name. */
  command_key: string;
  label: string;
  status: CommandRunStatus;
  /** Customer the run acted for; null = platform-wide (Helix operators only). */
  customer_name: string | null;
  arguments: Record<string, string | number | boolean | null>;
  /** Headline figures (e.g. waiting / acknowledged / skipped / failed). */
  summary: CommandResultTile[];
  error: string;
  started_at: string | null;
  finished_at: string | null;
  duration_seconds: number | null;
}

/** A run with its full report and result table. */
export interface ScheduledJobRunDetail extends ScheduledJobRun {
  stdout: string;
  stderr: string;
  result: CommandResult;
}

export interface ScheduledJobsResponse {
  summary: {
    total: number;
    failed: number;
    last_run_at: string | null;
    window_days: number;
    retention_days: number;
  };
  /** Jobs that ran in the window — the filter's options. */
  jobs: { key: string; label: string }[];
  count: number;
  page: number;
  page_size: number;
  results: ScheduledJobRun[];
}

export const scheduledJobsAPI = {
  /** Runs for one customer, newest first. */
  getRuns: (
    customerId: number,
    opts?: { command?: string; status?: string; days?: number; page?: number; pageSize?: number },
  ) =>
    api.get<ScheduledJobsResponse>('/imports/scheduled-jobs/', {
      params: {
        customer_id: customerId,
        ...(opts?.command ? { command: opts.command } : {}),
        ...(opts?.status ? { status: opts.status } : {}),
        ...(opts?.days ? { days: opts.days } : {}),
        ...(opts?.page ? { page: opts.page } : {}),
        ...(opts?.pageSize ? { page_size: opts.pageSize } : {}),
      },
    }),

  /** One run with its report and result table. */
  getRun: (customerId: number, runId: number) =>
    api.get<ScheduledJobRunDetail>(`/imports/scheduled-jobs/${runId}/`, {
      params: { customer_id: customerId },
    }),
};
